import React from 'react';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { displayPrice } from '../../util/configHelpers';
import { lazyLoadWithDimensions } from '../../util/uiHelpers';
import { formatMoney } from '../../util/currency';
import { ensureListing, ensureUser } from '../../util/data';
import { richText } from '../../util/richText';
import { createSlug } from '../../util/urlHelpers';
import { isBookingProcessAlias } from '../../transactions/transaction';

import { AspectRatioWrapper, NamedLink, ResponsiveImage } from '../../components';

import css from './ListingCard.module.css';
import { formatDimensions } from '../../util/genericHelper';
import Icons from '../Icons/Icons';

const MIN_LENGTH_FOR_LONG_WORDS = 10;

const priceData = (price, currency, intl) => {
  if (price && price.currency === currency) {
    const formattedPrice = formatMoney(intl, price);
    return { formattedPrice, priceTitle: formattedPrice };
  } else if (price) {
    return {
      formattedPrice: intl.formatMessage(
        { id: 'ListingCard.unsupportedPrice' },
        { currency: price.currency }
      ),
      priceTitle: intl.formatMessage(
        { id: 'ListingCard.unsupportedPriceTitle' },
        { currency: price.currency }
      ),
    };
  }
  return {};
};

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const PriceMaybe = props => {
  const { price, publicData, config, intl } = props;
  const { listingType } = publicData || {};
  const validListingTypes = config.listing.listingTypes;
  const foundListingTypeConfig = validListingTypes.find(conf => conf.listingType === listingType);
  const showPrice = displayPrice(foundListingTypeConfig);
  if (!showPrice && price) {
    return null;
  }

  const isBookable = isBookingProcessAlias(publicData?.transactionProcessAlias);
  const { formattedPrice, priceTitle } = priceData(price, config.currency, intl);
  return (
    <div className={css.price}>
      <div className={css.priceValue} title={priceTitle}>
        {formattedPrice}
      </div>
      {isBookable ? (
        <div className={css.perUnit}>
          <FormattedMessage id="ListingCard.perUnit" values={{ unitType: publicData?.unitType }} />
        </div>
      ) : null}
    </div>
  );
};

/**
 * ListingCard
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {Object} props.listing API entity: listing or ownListing
 * @param {string?} props.renderSizes for img/srcset
 * @param {Function?} props.setActiveListing
 * @param {boolean?} props.showAuthorInfo
 * @param {Object?} props.currentUser current user for favorites functionality
 * @param {Function?} props.onToggleFavorites function to toggle favorites
 * @param {Array?} props.pendingFavorites array of listing IDs that are pending to be favorited
 * @returns {JSX.Element} listing card to be used in search result panel etc.
 */
export const ListingCard = props => {
  const config = useConfiguration();
  const intl = props.intl || useIntl();
  const {
    className,
    rootClassName,
    listing,
    renderSizes,
    setActiveListing,
    showAuthorInfo = true,
    currentUser,
    onToggleFavorites,
    pendingFavorites = [],
  } = props;
  const classes = classNames(rootClassName || css.root, className);
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', price, publicData } = currentListing.attributes;
  const slug = createSlug(title);
  const author = ensureUser(listing.author);
  const authorName = author.attributes.profile.displayName;
  const firstImage =
    currentListing.images && currentListing.images.length > 0 ? currentListing.images[0] : null;

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  const setActivePropsMaybe = setActiveListing
    ? {
        onMouseEnter: () => setActiveListing(currentListing.id),
        onMouseLeave: () => setActiveListing(null),
      }
    : null;

  // Check if listing is in favorites
  const favorites = currentUser?.attributes?.profile?.publicData?.favorites || [];
  const isFavorite = favorites.includes(id);
  const isPendingFavorite = pendingFavorites.includes(id);
  const showAsFavorite = isFavorite || isPendingFavorite;

  const handleFavoriteClick = e => {
    e.preventDefault();
    e.stopPropagation();

    try {
      if (!currentUser) {
        if (onToggleFavorites) {
          onToggleFavorites(isFavorite);
        }
        return;
      }

      if (onToggleFavorites) {
        onToggleFavorites(isFavorite);
      }
    } catch (error) {
      console.error('Error toggling favorites:', error);
    }
  };

  return (
    <div className={classes}>
      <NamedLink name="ListingPage" params={{ id, slug }}>
        <AspectRatioWrapper
          className={css.aspectRatioWrapper}
          width={aspectWidth}
          height={aspectHeight}
          {...setActivePropsMaybe}
        >
          <LazyImage
            rootClassName={css.rootForImage}
            alt={title}
            image={firstImage}
            variants={variants}
            sizes={renderSizes}
          />
          {formatDimensions(publicData) ? (
            <p className={css.productValue}>{formatDimensions(publicData)}</p>
          ) : null}
        </AspectRatioWrapper>
      </NamedLink>
      <button
        className={classNames(css.favoriteButton, { [css.pending]: isPendingFavorite })}
        onClick={handleFavoriteClick}
        type="button"
        aria-label={intl.formatMessage({
          id: showAsFavorite ? 'ListingCard.removeFromFavorites' : 'ListingCard.addToFavorites',
        })}
        title={
          isPendingFavorite ? intl.formatMessage({ id: 'ListingCard.pendingFavorite' }) : undefined
        }
      >
        {showAsFavorite ? <Icons name="favorite" /> : <Icons name="unfavorite" />}
      </button>
      <div className={css.info}>
        <div className={css.mainInfo}>
          <h4 className={css.title}>
            {richText(title, {
              longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
              longWordClass: css.longWord,
            })}
          </h4>
          {/* {showAuthorInfo ? (
            <div className={css.authorInfo}>
              <FormattedMessage id="ListingCard.author" values={{ authorName }} />
            </div>
          ) : null} */}
        </div>
        <PriceMaybe price={price} publicData={publicData} config={config} intl={intl} />
      </div>
    </div>
  );
};

export default ListingCard;
